import { BodyWidget, TableWidget, TableRowWidget, ElementBox, Page } from '../viewer/page';
import { TextPosition } from '../selection/selection-helper';
import { isNullOrUndefined, L10n } from '@syncfusion/ej2-base';
import { WSectionFormat } from '../format/section-format';
import { XmlHttpRequestHandler, actionCompleteEvent } from '../../base/index';
import { HistoryInfo } from '../editor-history/index';
import { DialogUtility } from '@syncfusion/ej2-popups';
import { showSpinner, hideSpinner } from '@syncfusion/ej2-popups';
import { WCharacterFormat, WRowFormat } from '../format/index';
/**
 * Represents the collaborative editing module
 */
export class CollaborativeEditing {
    constructor(editor) {
        this.version = 0;
        this.owner = editor;
    }
    //private lockEnd: string = '';
    get documentHelper() {
        return this.owner.documentHelper;
    }
    get selection() {
        return this.owner.selectionModule;
    }
    get collaborativeEditingSettings() {
        return this.owner.documentEditorSettings.collaborativeEditingSettings;
    }
    getModuleName() {
        return 'CollaborativeEditing';
    }
    /**
     * To update the action which need to perform.
     *
     * @param {CollaborativeEditingEventArgs} data Specifies the data.
     * @returns {void}
     */
    updateAction(data) {
        if (!Array.isArray(data)) {
            data = [data];
        }
        for (let i = 0; i < data.length; i++) {
            const documentData = data[parseInt(i.toString(), 10)];
            switch (documentData.action) {
                case 'LockContent':
                    // Transform position
                    this.transFormLockRegion(documentData);
                    this.lockRegion(documentData.selectionInfo.start, documentData.selectionInfo.end, documentData.author);
                    break;
                case 'SaveContent':
                    this.version = documentData.version;
                    this.updateRegion(documentData.author, documentData.data);
                    break;
                case 'UnlockContent':
                    this.version = documentData.version;
                    this.updateRegion(documentData.author, documentData.data);
                    this.removeEditRange(documentData.author);
                    break;
            }
        }
    }
    transFormLockRegion(data) {
        const previousLockInfo = data.selectionInfo.previousLockInfo;
        if (!isNullOrUndefined(previousLockInfo)) {
            const author = previousLockInfo.author;
            let sectionDiff = 0;
            let blockDiff = 0;
            if (this.documentHelper.editRanges.containsKey(author)) {
                const editRange = this.documentHelper.editRanges.get(author);
                if (editRange.length > 0) {
                    const position = this.selection.getPosition(editRange[0]);
                    const endOffset = this.selection.getHierarchicalIndexByPosition(position.endPosition).split(';');
                    const previousOffset = previousLockInfo.end.split(';');
                    sectionDiff = parseInt(endOffset[0], 10) - parseInt(previousOffset[0], 10);
                    blockDiff = parseInt(endOffset[1], 10) - parseInt(previousOffset[1], 10);
                    // Same section
                    if (data.selectionInfo.start.split(';')[0] === previousLockInfo.end.split(';')[0]) {
                        data.selectionInfo.start = this.tranformPosition(data.selectionInfo.start, sectionDiff, blockDiff);
                        data.selectionInfo.end = this.tranformPosition(data.selectionInfo.end, sectionDiff, blockDiff);
                    }
                    else {
                        data.selectionInfo.start = this.tranformPosition(data.selectionInfo.start, sectionDiff, 0);
                        data.selectionInfo.end = this.tranformPosition(data.selectionInfo.end, sectionDiff, 0);
                    }
                }
            }
        }
    }
    /**
     * Lock selected region from editing by other users.
     *
     * @param {string} user Specifies the user.
     * @returns {void}
     */
    lockContent(user) {
        if (this.canLock()) {
            let start = this.owner.selectionModule.start;
            let end = this.owner.selectionModule.end;
            if (!this.owner.selectionModule.isForward) {
                start = this.owner.selectionModule.end;
                end = this.owner.selectionModule.start;
            }
            if (start.paragraph.isInsideTable) {
                const table = this.owner.documentHelper.layout.getParentTable(start.paragraph);
                const firstPara = this.owner.documentHelper.getFirstParagraphBlock(table);
                start.setPosition(firstPara.childWidgets[0], true);
            }
            else {
                start.paragraphStartInternal(this.owner.selectionModule, false);
            }
            if (end.paragraph.isInsideTable) {
                const table = this.owner.documentHelper.layout.getParentTable(end.paragraph);
                const lastPara = this.owner.documentHelper.getLastParagraphBlock(table);
                const offset = lastPara.lastChild.getEndOffset();
                end.setPositionParagraph(lastPara.lastChild, offset);
            }
            else {
                end.moveToParagraphEndInternal(this.owner.selectionModule, false);
            }
            const startOffset = this.owner.selectionModule.getHierarchicalIndexByPosition(start);
            const endOffset = this.owner.selectionModule.getHierarchicalIndexByPosition(end);
            const selectionInfo = {
                start: startOffset,
                end: endOffset,
                roomName: this.owner.documentEditorSettings.collaborativeEditingSettings.roomName,
                author: isNullOrUndefined(user) ? this.owner.currentUser : user,
                version: this.version
            };
            const startInfo = this.selection.getParagraphInfo(start);
            const endInfo = this.selection.getParagraphInfo(end);
            this.owner.selectionModule.select(startOffset, endOffset);
            const ajax = new XmlHttpRequestHandler();
            ajax.url = this.owner.serviceUrl + this.owner.serverActionSettings.canLock;
            ajax.contentType = 'application/json;charset=UTF-8';
            ajax.onSuccess = (result) => {
                this.successHandler(result, selectionInfo, startInfo, endInfo);
            };
            ajax.onFailure = this.failureHandler.bind(this);
            ajax.onError = this.failureHandler.bind(this);
            ajax.customHeaders = this.owner.headers;
            ajax.send(selectionInfo);
        }
    }
    /**
     * @private
     * @returns {boolean} - Returns can lock.
     */
    canLock() {
        const editRanges = this.documentHelper.editRanges;
        if (editRanges.containsKey(this.owner.currentUser)) {
            return false;
        }
        const userNames = editRanges.keys;
        for (let i = 0; i < userNames.length; i++) {
            const range = editRanges.get(userNames[parseInt(i.toString(), 10)]);
            if (!isNullOrUndefined(range) && range.length > 0) {
                if (this.isSelectionInEditableRange(range[0])) {
                    return false;
                }
            }
        }
        return true;
    }
    getPreviousLockedRegion() {
        const editRanges = this.documentHelper.editRanges;
        if (editRanges.containsKey(this.owner.currentUser)) {
            return undefined;
        }
        let previous;
        const userNames = editRanges.keys;
        for (let i = 0; i < userNames.length; i++) {
            const range = editRanges.get(userNames[parseInt(i.toString(), 10)])[0];
            const startPosition = this.selection.getPosition(range).startPosition;
            if (startPosition.isExistBefore(this.selection.start)) {
                if (isNullOrUndefined(previous)) {
                    previous = range;
                }
                else if (startPosition.isExistAfter(this.selection.getPosition(previous).startPosition)) {
                    previous = range;
                }
            }
        }
        return previous;
    }
    /**
     * @private
     * @param {string} user - Specifies the user.
     * @returns {void}
     */
    unlockContent(user) {
        if (this.documentHelper.editRanges.containsKey(user)) {
            if (this.saveTimer) {
                clearTimeout(this.saveTimer);
            }
            const sfdtContent = JSON.stringify(this.serializeEditableRegion(user));
            const saveObject = {
                action: 'UnlockContent',
                author: user,
                version: this.version,
                data: sfdtContent,
                selectionInfo: {
                    start: '',
                    end: '',
                    roomName: this.collaborativeEditingSettings.roomName,
                    author: this.owner.currentUser,
                    version: this.version
                },
                roomName: this.collaborativeEditingSettings.roomName
            };
            this.removeEditRange(user);
            this.owner.editorHistoryModule.clearHistory();
            this.owner.fireContentChange();
            // Todo: selection tranformation
            this.owner.trigger(actionCompleteEvent, saveObject);
        }
    }
    removeEditRange(user) {
        if (this.documentHelper.editRanges.containsKey(user)) {
            this.updateLockRegion(user, false);
            this.owner.editorModule.removeUserRestrictionsInternal(this.documentHelper.editRanges.get(user)[0]);
            this.documentHelper.clearContent();
            this.selection.updateEditRangeCollection();
            this.documentHelper.owner.viewer.updateScrollBars();
        }
    }
    /**
     * Save locked content to other clients.
     *
     * @private
     * @returns {void}
     */
    saveContent() {
        if (this.saveTimer) {
            clearTimeout(this.saveTimer);
        }
        let timeOut = this.collaborativeEditingSettings.saveTimeout;
        if (isNullOrUndefined(timeOut)) {
            timeOut = 3000;
        }
        this.saveTimer = Number(setTimeout(() => {
            this.saveContentInternal();
        }, timeOut));
    }
    saveContentInternal() {
        if (this.documentHelper.editRanges.containsKey(this.owner.currentUser)) {
            const editRangeStart = this.documentHelper.editRanges.get(this.owner.currentUser)[0];
            const position = this.selection.getPosition(editRangeStart);
            const saveObject = {
                action: 'SaveContent',
                author: this.owner.currentUser,
                version: this.version,
                data: JSON.stringify(this.serializeEditableRegion(this.owner.currentUser)),
                selectionInfo: {
                    'start': this.selection.getHierarchicalIndexByPosition(position.startPosition),
                    'end': this.selection.getHierarchicalIndexByPosition(position.endPosition),
                    roomName: this.collaborativeEditingSettings.roomName,
                    author: this.owner.currentUser,
                    version: this.version
                },
                roomName: this.collaborativeEditingSettings.roomName
            };
            this.owner.trigger(actionCompleteEvent, saveObject);
        }
    }
    serializeEditableRegion(user) {
        const startElement = this.documentHelper.editRanges.get(user)[0];
        const endElement = startElement.editRangeEnd;
        const start = new TextPosition(this.owner);
        start.setPosition(startElement.line, true);
        const end = new TextPosition(this.owner);
        end.setPosition(endElement.line, false);
        this.owner.sfdtExportModule.isPartialExport = true;
        /* eslint-disable-next-line max-len */
        const sfdtContent = this.owner.sfdtExportModule.write(0, start.currentWidget, start.offset, end.currentWidget, end.offset, false);
        this.owner.sfdtExportModule.isPartialExport = false;
        return sfdtContent;
    }
    /* eslint-disable-next-line max-len */
    successHandler(result, selectionInfo, startInfo, endInfo) {
        const canLock = JSON.parse(result.data).canLock;
        if (canLock) {
            selectionInfo.start = this.selection.getHierarchicalIndex(startInfo.paragraph, startInfo.offset.toString());
            selectionInfo.end = this.selection.getHierarchicalIndex(endInfo.paragraph, endInfo.offset.toString());
            const previousEditRange = this.getPreviousLockedRegion();
            if (previousEditRange) {
                const position = this.selection.getPosition(previousEditRange);
                selectionInfo.previousLockInfo = {
                    start: this.selection.getHierarchicalIndexByPosition(position.startPosition),
                    end: this.selection.getHierarchicalIndexByPosition(position.endPosition),
                    author: previousEditRange.user,
                    roomName: '',
                    version: 0
                };
            }
            const lockObject = {
                action: 'LockContent',
                selectionInfo: selectionInfo,
                author: this.owner.currentUser,
                version: this.version,
                data: '',
                roomName: this.collaborativeEditingSettings.roomName
            };
            this.owner.trigger(actionCompleteEvent, lockObject);
        }
        else {
            const localizeValue = new L10n('documenteditor', this.owner.defaultLocale);
            localizeValue.setLocale(this.owner.locale);
            DialogUtility.alert({
                content: localizeValue.getConstant('Already locked'),
                closeOnEscape: true, showCloseIcon: true, position: { X: 'Center', Y: 'Center' }
            }).enableRtl = this.owner.enableRtl;
        }
    }
    failureHandler() {
        const localizeValue = new L10n('documenteditor', this.owner.defaultLocale);
        localizeValue.setLocale(this.owner.locale);
        DialogUtility.alert({
            content: localizeValue.getConstant('Error in establishing connection with web server'),
            closeOnEscape: true, showCloseIcon: true, position: { X: 'Center', Y: 'Center' }
        }).enableRtl = this.owner.enableRtl;
    }
    /**
     * Locker specified region for specified user.
     *
     * @private
     * @param {string} start - Specified the selection start.
     * @param {string} end - Specifies the selection end.
     * @param {string} user - Specifies the user
     * @returns {void}
     */
    lockRegion(start, end, user) {
        const startPosition = this.selection.getTextPosBasedOnLogicalIndex(start);
        const endPosition = this.selection.getTextPosBasedOnLogicalIndex(end);
        this.lockRegionInternal(startPosition, endPosition, user);
    }
    lockRegionInternal(start, end, user) {
        const editStart = this.owner.editorModule.addEditElement(user);
        const editEnd = editStart.editRangeEnd;
        this.insertElements(start, end, [editEnd], [editStart]);
        this.updateLockInfo(editStart.paragraph, editEnd.paragraph, user, true);
        this.owner.viewer.updateScrollBars();
    }
    insertElements(start, end, endElements, startElements) {
        const isSamePara = start.paragraph.equals(end.paragraph);
        if (!isNullOrUndefined(startElements)) {
            this.insertElementsInternal(start, startElements);
        }
        if (!isNullOrUndefined(endElements)) {
            this.insertElementsInternal(end, endElements);
        }
        this.documentHelper.layout.reLayoutParagraph(startElements[0].paragraph, 0, 0);
        if (!isSamePara) {
            this.documentHelper.layout.reLayoutParagraph(endElements[0].paragraph, 0, 0);
        }
    }
    insertElementsInternal(position, elements) {
        let indexInInline = 0;
        if (position.paragraph.isEmpty()) {
            const paragraph = position.paragraph;
            paragraph.childWidgets[0].children.push(elements[0]);
            elements[0].line = paragraph.childWidgets[0];
        }
        else {
            const inlineObj = position.currentWidget.getInline(position.offset, indexInInline);
            const curInline = inlineObj.element;
            indexInInline = inlineObj.index;
            const firstElement = elements[0];
            this.insertElementInternal(curInline, firstElement, indexInInline);
            const index = firstElement.indexInOwner;
            let lastElement = firstElement;
            for (let i = 1; i < elements.length; i++) {
                lastElement = elements[parseInt(i.toString(), 10)];
                firstElement.line.children.splice(index + i, 0, lastElement);
            }
        }
    }
    insertElementInternal(element, newElement, index) {
        const line = element.line;
        const paragraph = line.paragraph;
        let insertIndex = element.indexInOwner;
        const isBidi = paragraph.paragraphFormat.bidi && element.isRightToLeft;
        if (index === element.length) {
            // Add new Element in current
            if (!isBidi) {
                insertIndex++;
            }
            line.children.splice(insertIndex, 0, newElement);
        }
        else if (index === 0) {
            if (isNullOrUndefined(element.previousNode)) {
                line.children.splice(0, 0, newElement);
                insertIndex = 0;
            }
            else {
                line.children.splice(insertIndex, 0, newElement);
            }
        }
        newElement.line = element.line;
    }
    //#region Save content
    setEditableRegion() {
        if (this.documentHelper.editRanges.containsKey(this.owner.currentUser)) {
            const editRanges = this.documentHelper.editRanges.get(this.owner.currentUser);
            const editRangeStart = editRanges[0];
            const firstBlock = this.getParentBlock(editRangeStart.paragraph);
            this.lockStart = this.owner.selectionModule.getHierarchicalIndex(firstBlock, '0').split(';');
        }
    }
    isSelectionInEditableRange(editRange) {
        if (!isNullOrUndefined(this.owner.selectionModule)) {
            let start = this.owner.selectionModule.start;
            let end = this.owner.selectionModule.end;
            if (!this.owner.selectionModule.isForward) {
                [start, end] = [end, start];
            }
            const position = this.owner.selectionModule.getPosition(editRange);
            if ((start.isExistAfter(position.startPosition) || start.isAtSamePosition(position.startPosition))
                && (end.isExistBefore(position.endPosition) || end.isAtSamePosition(position.endPosition)) ||
                ((position.startPosition.isExistAfter(start) || position.startPosition.isAtSamePosition(start))
                    && (position.endPosition.isExistBefore(end) || position.endPosition.isAtSamePosition(end))) ||
                (position.startPosition.isExistAfter(start) && position.startPosition.isExistBefore(end)
                    && (end.isExistAfter(position.endPosition) || end.isExistBefore(position.endPosition))) ||
                (position.endPosition.isExistBefore(end) && position.endPosition.isExistAfter(start)
                    && (start.isExistBefore(position.startPosition) || start.isExistAfter(position.startPosition)))) {
                return true;
            }
        }
        return false;
    }
    /**
     * Updated modified content from remote user
     *
     * @returns {void}
     */
    /* eslint-disable  */
    updateRegion(user, content) {
        if (this.documentHelper.editRanges.containsKey(user)) {
            let editRanges = this.documentHelper.editRanges.get(user);
            if (editRanges.length === 1) {
                // Remove exisiting range  from collection.
                // New range will get inserted while updating the content.
                this.documentHelper.editRanges.remove(user);
                editRanges[0].removeEditRangeMark();
            }
            // Preserve hierachical position for history position tranformation
            this.setEditableRegion();
            let startElement = editRanges[0];
            let endElement = startElement.editRangeEnd;
            let firstBlock = this.getParentBlock(startElement.paragraph);
            let lastBlock = this.getParentBlock(endElement.paragraph);
            let isInEditRange = this.isSelectionInEditableRange(startElement);
            let startParagrahInfo;
            let endParagrahInfo;
            this.owner.editorModule.isRemoveRevision = true;
            if (!isInEditRange) {
                startParagrahInfo = this.owner.selectionModule.getParagraphInfo(this.owner.selectionModule.start);
                endParagrahInfo = this.owner.selectionModule.getParagraphInfo(this.owner.selectionModule.end);
            }
            let sections = [];
            while (lastBlock !== firstBlock) {
                let currentBlock = lastBlock.combineWidget(this.owner.viewer);
                lastBlock = currentBlock.previousRenderedWidget;
                if (lastBlock.bodyWidget.index !== currentBlock.bodyWidget.index) {
                    sections.push(currentBlock.bodyWidget);
                }
                let removedBlock = currentBlock.containerWidget.childWidgets[currentBlock.indexInOwner];
                this.removeDuplicateCollection(removedBlock);
                currentBlock.containerWidget.removeChild(currentBlock.indexInOwner);
            }
            if (!isNullOrUndefined(firstBlock)) {
                let lastBockIndex = firstBlock.index;
                let containerWidget = firstBlock.containerWidget;
                sections.push(containerWidget);
                let lastInsertIndex = firstBlock.containerWidget.childWidgets.indexOf(firstBlock);
                let removedBlock = containerWidget.childWidgets[lastInsertIndex];
                containerWidget.removeChild(lastInsertIndex);
                this.removeDuplicateCollection(removedBlock);
                let comments = [];
                let blocks = [];
                let revision = [];
                this.owner.editorModule.isPasteListUpdated = false;
                this.owner.editorModule.getBlocks(JSON.parse(content), false, blocks, comments, revision);
                if (sections.length !== blocks.length) {
                    if (sections.length === 1) {
                        let bodyWidget = sections[0];
                        sections.unshift(this.owner.editorModule.splitBodyWidget(bodyWidget, blocks[blocks.length - 2].sectionFormat, bodyWidget.childWidgets[lastInsertIndex - 1]));
                    }
                    if (sections.length < blocks.length) {
                        for (let m = 1; m < blocks.length - 1; m++) {
                            let page = new Page(this.owner.documentHelper);
                            let bodyWidget = new BodyWidget();
                            page.bodyWidgets.push(bodyWidget);
                            bodyWidget.page = page;
                            sections.splice(m, 0, bodyWidget);
                            bodyWidget.index = sections[m - 1].index;
                            bodyWidget.sectionFormat = new WSectionFormat(bodyWidget);
                            bodyWidget.sectionFormat.copyFormat(blocks[m].sectionFormat);
                            let pageIndex = sections[m - 1].page.index;
                            this.documentHelper.insertPage(pageIndex, page);
                            //Todo: update section index
                            this.owner.editorModule.updateSectionIndex(sections[m - 1].sectionFormat, sections[m - 1], true);
                            if (sections.length === blocks.length) {
                                break;
                            }
                        }
                    }
                }
                for (let z = 0; z < sections.length; z++) {
                    let containerWidget = sections[z];
                    let blockIndex = 0;
                    let insertIndex = 0;
                    if (z === sections.length - 1) {
                        blockIndex = lastBockIndex;
                        insertIndex = lastInsertIndex;
                    }
                    let block = blocks[z].childWidgets;
                    for (let i = 0; i < block.length; i++) {
                        block[i].containerWidget = containerWidget;
                        block[i].index = blockIndex;
                        containerWidget.childWidgets.splice(insertIndex, 0, block[i]);
                        insertIndex++;
                        blockIndex++;
                    }
                    lastBlock = block[block.length - 1];
                    if (lastBlock.nextRenderedWidget && lastBlock.nextRenderedWidget.index !== lastBlock.index) {
                        //Todo: update block index properly
                        this.updateNextBlocksIndex(lastBlock, true);
                    }
                    this.documentHelper.layout.layoutBodyWidgetCollection(block[0].index, containerWidget, undefined, false);
                }
                for (let k = 0; k < comments.length; k++) {
                    let comment = comments[k];
                    this.owner.editorModule.addCommentWidget(comment, false, this.owner.showComments, false);
                    if (comment.replyComments.length > 0) {
                        for (let z = 0; z < comment.replyComments.length; z++) {
                            this.owner.commentReviewPane.addReply(comment.replyComments[z], false, false);
                        }
                    }
                }
                if (revision.length > 0) {
                    this.updateRevisionCollection(revision);
                }
                this.owner.trackChangesPane.updateTrackChanges();
                let editRanges = this.documentHelper.editRanges.get(user);
                // update content
                if (!isInEditRange) {
                    this.tranformSelection(startParagrahInfo, endParagrahInfo);
                }
                else {
                    if (editRanges.length > 0) {
                        let positionInfo = this.selection.getPosition(editRanges[0]);
                        // We can't able to predic the modified content inside editable region
                        // So, it not possible to transform the position relativly.
                        // So, move the selection to editable region end.
                        this.selection.selectPosition(positionInfo.endPosition, positionInfo.endPosition);
                    }
                }
                this.tranformHistoryPosition();
                this.selection.updateEditRangeCollection();
                this.updateLockRegion(user);
                this.documentHelper.removeEmptyPages();
                this.owner.viewer.updateScrollBars();
                this.owner.editorModule.isRemoveRevision = false;
            }
        }
    }
    updateRevisionCollection(revision) {
        let insertIndex = 0;
        let revisionStart = this.getRevisionTextPosition(revision[0]);
        let isInsert = false;
        if (this.owner.revisionsInternal.changes.length > 0 &&
            !isNullOrUndefined(revisionStart)) {
            for (let i = 0; i < this.owner.revisionsInternal.changes.length; i++) {
                let textPosition = this.getRevisionTextPosition(this.owner.revisionsInternal.changes[i]);
                if (textPosition.isExistAfter(revisionStart)) {
                    insertIndex = i;
                    isInsert = true;
                    break;
                }
            }
        }
        for (let j = 0; j < revision.length; j++) {
            this.owner.editor.updateRevisionCollection(revision[j]);
        }
    }
    /* eslint-disable @typescript-eslint/no-explicit-any */
    getRevisionTextPosition(revision) {
        if (revision.getRange().length > 0) {
            let range = revision.getRange()[0];
            if (range instanceof ElementBox) {
                return this.selection.getElementPosition(range).startPosition;
            }
            else if (range instanceof WRowFormat) {
                let block = range.ownerBase.firstChild.firstChild;
                if (block.bodyWidget) {
                    return this.selection.getTextPosBasedOnLogicalIndex(this.selection.getHierarchicalIndex(block, '0'));
                }
            }
            else if (range instanceof WCharacterFormat) {
                let paraWidget = range.ownerBase;
                if (paraWidget.lastChild.paragraph.bodyWidget) {
                    let offset = paraWidget.getLength();
                    let startPosition = new TextPosition(this.owner);
                    startPosition.setPositionParagraph(paraWidget.lastChild, offset);
                    return startPosition;
                }
            }
        }
        return undefined;
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */
    tranformSelection(startParagrahInfo, endParagraphInfo) {
        this.documentHelper.skipScrollToPosition = true;
        let startIndex = this.selection.getHierarchicalIndex(startParagrahInfo.paragraph, startParagrahInfo.offset.toString());
        let endIndex = this.selection.getHierarchicalIndex(endParagraphInfo.paragraph, endParagraphInfo.offset.toString());
        this.selection.select(startIndex, endIndex);
    }
    tranformHistoryPosition() {
        if (this.documentHelper.editRanges.containsKey(this.owner.currentUser)) {
            let editRanges = this.documentHelper.editRanges.get(this.owner.currentUser);
            let startElement = editRanges[0];
            let startBlock = this.getParentBlock(startElement.paragraph);
            let startOffset = this.selection.getHierarchicalIndex(startBlock, '0').split(';');
            if (!isNullOrUndefined(this.lockStart) && this.lockStart.length > 1) {
                let sectionDiff = parseInt(startOffset[0], 10) - parseInt(this.lockStart[0], 10);
                let blockDiff = parseInt(startOffset[1], 10) - parseInt(this.lockStart[1], 10);
                this.transformHistory(sectionDiff, blockDiff);
            }
        }
    }
    transformHistory(sectionDiff, blockDiff) {
        if (this.owner.enableEditorHistory) {
            let undoStack = this.owner.editorHistoryModule.undoStack;
            if (!isNullOrUndefined(undoStack)) {
                for (let i = 0; i < undoStack.length; i++) {
                    this.transformBaseHistoryInfo(undoStack[i], sectionDiff, blockDiff);
                }
            }
            let redoStack = this.owner.editorHistoryModule.redoStack;
            if (!isNullOrUndefined(redoStack)) {
                for (let i = 0; i < redoStack.length; i++) {
                    this.transformBaseHistoryInfo(redoStack[i], sectionDiff, blockDiff);
                }
            }
        }
    }
    transformBaseHistoryInfo(baseHistoryInfo, sectionDiff, blockDiff) {
        if (baseHistoryInfo.endPosition) {
            baseHistoryInfo.endPosition = this.tranformPosition(baseHistoryInfo.endPosition, sectionDiff, blockDiff);
        }
        if (baseHistoryInfo.insertPosition) {
            baseHistoryInfo.insertPosition = this.tranformPosition(baseHistoryInfo.insertPosition, sectionDiff, blockDiff);
        }
        if (baseHistoryInfo.selectionStart) {
            baseHistoryInfo.selectionStart = this.tranformPosition(baseHistoryInfo.selectionStart, sectionDiff, blockDiff);
        }
        if (baseHistoryInfo.selectionEnd) {
            baseHistoryInfo.selectionEnd = this.tranformPosition(baseHistoryInfo.selectionEnd, sectionDiff, blockDiff);
        }
        if (baseHistoryInfo instanceof HistoryInfo) {
            let modifiedActions = baseHistoryInfo.modifiedActions;
            for (let j = 0; j < modifiedActions.length; j++) {
                this.transformBaseHistoryInfo(modifiedActions[j], sectionDiff, blockDiff);
            }
        }
    }
    tranformPosition(position, sectionDiff, blockDiff) {
        let index = position.split(';');
        index[0] = (parseInt(index[0], 10) + sectionDiff).toString();
        index[1] = (parseInt(index[1], 10) + blockDiff).toString();
        return index.join(';');
    }
    getParentBlock(block) {
        if (block.isInsideTable) {
            block = this.owner.documentHelper.layout.getParentTable(block);
        }
        return block.combineWidget(this.owner.viewer);
    }
    //#endregion
    //#region Remove existing items in locked region
    removeDuplicateCollection(removedBlock) {
        this.removeFieldInBlock(removedBlock, false, false);
        this.removeFieldInBlock(removedBlock, true, false);
        this.removeFieldInBlock(removedBlock, false, true);
        if (removedBlock instanceof TableWidget) {
            for (let i = 0; i < removedBlock.childWidgets.length; i++) {
                if (removedBlock.childWidgets[i] instanceof TableRowWidget) {
                    let tableDelete = removedBlock.childWidgets[i];
                    this.owner.editorModule.removeRevisionsInRow(tableDelete, false);
                }
            }
        }
        else {
            this.owner.editorModule.removeRevisionForBlock(removedBlock, false, false);
        }
    }
    removeFieldInBlock(block, isBookmark, contentControl) {
        if (block instanceof TableWidget) {
            this.removeFieldTable(block, isBookmark, contentControl);
        }
        else {
            this.owner.editorModule.removeField(block, isBookmark, contentControl);
            this.removeComment(block);
        }
    }
    removeFieldTable(table, isBookmark, contentControl) {
        for (let i = 0; i < table.childWidgets.length; i++) {
            let rowWidget = table.childWidgets[i];
            for (let j = 0; j < rowWidget.childWidgets.length; j++) {
                let widget = rowWidget.childWidgets[j];
                for (let i = 0; i < widget.childWidgets.length; i++) {
                    this.removeFieldInBlock(widget.childWidgets[i], isBookmark, contentControl);
                }
            }
        }
    }
    removeComment(block) {
        if (this.documentHelper.comments.length > 0) {
            for (let i = 0; i < this.documentHelper.comments.length; i++) {
                let comment = this.documentHelper.comments[i];
                if (comment.commentStart.line.paragraph === block) {
                    this.documentHelper.comments.splice(i, 1);
                    this.owner.commentReviewPane.deleteComment(comment);
                    i--;
                }
            }
        }
    }
    //#endregion
    updateNextBlocksIndex(block, increaseIndex) {
        let nextBlock = block.getSplitWidgets().pop().nextRenderedWidget;
        let incrementCount = 1;
        if (nextBlock.bodyWidget.index === block.bodyWidget.index) {
            incrementCount = block.index - nextBlock.index + 1;
        }
        let nextIndex = block.containerWidget.childWidgets.indexOf(block) + 1;
        if (block.containerWidget instanceof BodyWidget) {
            let sectionIndex = block.containerWidget.index;
            let pageIndex = this.documentHelper.pages.indexOf(block.containerWidget.page);
            for (let j = pageIndex; j < this.documentHelper.pages.length; j++) {
                let page = this.documentHelper.pages[j];
                if (page.bodyWidgets[0].index === sectionIndex) {
                    for (let k = nextIndex; k < page.bodyWidgets[0].childWidgets.length; k++) {
                        let childWidget = page.bodyWidgets[0].childWidgets[k];
                        childWidget.index += incrementCount;
                    }
                    nextIndex = 0;
                }
                else {
                    return;
                }
            }
        }
    }
    /**
     * Update locked region highlight.
     *
     * @private
     * @param {string} user - Specified the user.
     * @param {boolean} isLocked - Specifies the isLocked.
     * @returns {void}
     */
    updateLockRegion(user, isLocked) {
        if (isNullOrUndefined(user)) {
            user = this.owner.currentUser;
        }
        isLocked = isNullOrUndefined(isLocked) ? true : isLocked;
        if (this.documentHelper.editRanges.containsKey(user)) {
            let editRanges = this.documentHelper.editRanges.get(user);
            if (editRanges.length === 1 && !isNullOrUndefined(editRanges[0].editRangeEnd)) {
                let editStart = editRanges[0];
                this.updateLockInfo(editStart.paragraph, editStart.editRangeEnd.paragraph, user, isLocked);
            }
        }
    }
    updateLockInfo(startBlock, endBlock, user, locked) {
        if (startBlock.isInsideTable) {
            startBlock = this.documentHelper.layout.getParentTable(startBlock);
        }
        if (endBlock.isInsideTable) {
            endBlock = this.documentHelper.layout.getParentTable(endBlock);
        }
        do {
            if (locked) {
                startBlock.lockedBy = user;
                startBlock.locked = locked;
            }
            else {
                startBlock.lockedBy = undefined;
                startBlock.locked = locked;
            }
            if (startBlock === endBlock) {
                break;
            }
            startBlock = startBlock.nextRenderedWidget;
            if (isNullOrUndefined(startBlock)) {
                break;
            }
        } while (startBlock);
    }
    /**
     * Pull pending actions from server.
     *
     * @returns {void}
     */
    pullAction() {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        if (this.owner) {
            let ajax = new XmlHttpRequestHandler();
            ajax.url = this.owner.serviceUrl + this.owner.serverActionSettings.getPendingActions;
            ajax.contentType = 'application/json;charset=UTF-8';
            ajax.onSuccess = (result) => {
                if (typeof result === 'string') {
                    result = JSON.parse(result);
                }
                this.updateAction(JSON.parse(result.data));
                hideSpinner(this.owner.element);
            };
            ajax.onFailure = this.failureHandler.bind(this);
            ajax.onError = this.failureHandler.bind(this);
            ajax.customHeaders = this.owner.headers;
            showSpinner(this.owner.element);
            ajax.send(({ 'roomName': this.collaborativeEditingSettings.roomName, version: this.version }));
        }
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */
    /**
     * Destroy collaborative editing module.
     *
     * @returns {void}
     */
    destroy() {
        this.owner = undefined;
    }
}
